import { CountryIcon, FlexGrid, Header, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { COUNTRY_CODES } from '../../constants/index';
import SimpleHelmet from '../SimpleHelmet';
import styles from './LegalPageHeader.module.css';

const cx = classnames.bind(styles);

const ThreeLetterCountryCodes = {
  US: 'USA',
  CA: 'CAN',
};

const CountryNames = {
  US: 'USA',
  CA: 'Canada',
};

const CountryCodes = Object.values(COUNTRY_CODES);

export default function LegalPageHeader({
  canonicalPath,
  country,
  effectiveDate,
  renderCountrySpecificTitle,
  title,
  tableOfContents,
}) {
  const { t } = useTranslation('legal');
  const contentType = canonicalPath.split('/')[1];
  const usableCountry = CountryCodes.includes(country) ? country : COUNTRY_CODES.USA;
  const unselectedCountryCodes = CountryCodes.filter(c => c !== usableCountry);
  const helmetTitle = renderCountrySpecificTitle ? `${title}, ${CountryNames[usableCountry]}` : title;
  const cleanDescription = title?.replace(/\s-/, '\'s');
  const helmetDescription = renderCountrySpecificTitle
    ? t(`in${CountryNames[usableCountry]}Market`, { ns: 'legal' })
    : cleanDescription;

  return (
    <Fragment>
      <SimpleHelmet
        canonicalPath={canonicalPath}
        description={`${helmetDescription}. ${t('effectiveDate', { ns: 'legal', date: effectiveDate })}`}
        title={helmetTitle}
      />
      <FlexGrid container>
        <FlexGrid.Column tablet={country ? 8 : 12} desktop={country ? 9 : 12}>
          {!!country && (
            <CountryIcon
              countryCode={ThreeLetterCountryCodes[usableCountry]}
              size="xl"
            />
          )}
          {tableOfContents}
          {!!title && (
            <Header
              className={cx('title', { noCountryList: !country })}
              color="grey"
              headline
            >
              {title}
            </Header>
          )}
        </FlexGrid.Column>
        {!!country && (
          <FlexGrid.Column tablet={4} desktop={3} className={cx('otherCountryFlags')}>
            <Text>
              {t('switchTo', { ns: 'legal' })}
            </Text>
            {unselectedCountryCodes.map(countryCode => (
              <Link
                key={countryCode}
                to={`/${contentType}/${ThreeLetterCountryCodes[countryCode].toLowerCase()}`}
              >
                <CountryIcon
                  countryCode={ThreeLetterCountryCodes[countryCode]}
                  size="xl"
                />
              </Link>
            ))}
          </FlexGrid.Column>
        )}
      </FlexGrid>
    </Fragment>
  );
}

LegalPageHeader.propTypes = {
  canonicalPath: PropTypes.string,
  country: PropTypes.oneOf(['US', 'CA', undefined]),
  effectiveDate: PropTypes.string,
  renderCountrySpecificTitle: PropTypes.bool,
  title: PropTypes.string,
  tableOfContents: PropTypes.node,
};

LegalPageHeader.defaultProps = {
  canonicalPath: '/privacy',
  country: undefined,
  effectiveDate: undefined,
  renderCountrySpecificTitle: false,
  title: undefined,
  tableOfContents: undefined,
};
